import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader } from "@/components/common/loader";
import { MonitorDialog } from "@/features/eservices/components/monitor-dialog";
import { deleteMonitor, listMonitors } from "@/features/eservices/api";
import type { TranscriptMonitor } from "@/features/eservices/types";
import { useTranscriptsContext } from "./transcripts-layout";

const FORM_LABEL: Record<string, string> = {
  SUMMARY: "Account summary",
  INCOME: "Wage & income",
};

export function TranscriptMonitoringPage() {
  const { client } = useTranscriptsContext();
  const [rows, setRows] = useState<TranscriptMonitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<{ open: boolean; edit: TranscriptMonitor | null }>({
    open: false,
    edit: null,
  });

  const inactive = client.status === "INACTIVE";

  const refresh = useCallback(() => {
    setLoading(true);
    listMonitors(client.id)
      .then(setRows)
      .catch(() => toast.error("Could not load monitors."))
      .finally(() => setLoading(false));
  }, [client.id]);

  useEffect(refresh, [refresh]);

  const remove = async (id: string) => {
    try {
      await deleteMonitor(client.id, id);
      refresh();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {inactive && (
        <Alert>
          <AlertTitle>Inactive client</AlertTitle>
          <AlertDescription>
            We do not monitor transcripts for inactive clients.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-foreground text-sm font-semibold">Transcript Monitoring</h3>
        <Button
          size="sm"
          disabled={inactive}
          onClick={() => setDialog({ open: true, edit: null })}
        >
          Add monitor
        </Button>
      </div>

      <Alert>
        <AlertTitle>How monitoring works</AlertTitle>
        <AlertDescription>
          Active monitors are checked weekly (Tuesday mornings) with auto-connect
          on. We never overwrite a newer TDS transcript or a hand-entered record.
        </AlertDescription>
      </Alert>

      {loading ? (
        <Loader />
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">No monitors configured.</p>
      ) : (
        <Card className="gap-0 overflow-hidden p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Transcript</TableHead>
                <TableHead>For</TableHead>
                <TableHead>Years</TableHead>
                <TableHead>Last Check</TableHead>
                <TableHead>Notify</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="font-medium">
                    {FORM_LABEL[m.form] ?? m.form}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{m.forWho}</TableCell>
                  <TableCell>
                    {[m.yearFrom, m.yearTo].filter(Boolean).join("–") || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {m.lastCheck ? new Date(m.lastCheck).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell>{m.notify}</TableCell>
                  <TableCell>
                    <Badge variant={m.isActive ? "default" : "outline"}>
                      {m.isActive ? "Active" : "Off"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDialog({ open: true, edit: m })}
                    >
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(m.id)}>
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <MonitorDialog
        open={dialog.open}
        edit={dialog.edit}
        clientId={client.id}
        hasSpouse={Boolean(client.spouse)}
        onOpenChange={(open) => setDialog({ open, edit: open ? dialog.edit : null })}
        onSaved={() => {
          setDialog({ open: false, edit: null });
          refresh();
        }}
      />
    </div>
  );
}
